"use client";
import React, { useEffect } from "react";
import { Block, blockStyles } from "../types/screenplay-editor.types";
import { Loader2, FileText } from "lucide-react";

export function SceneScriptView({
  blocks,
  isLoading = false,
  sceneNumber,
  title,
}: {
  blocks: Block[];
  isLoading?: boolean;
  sceneNumber?: string;
  title?: string;
}) {
  const COURIER_STACK = "'Courier Prime', 'Courier', monospace";

  useEffect(() => {
    const fontId = "google-font-courier-prime";
    const styleId = "screenplay-editor-force-v4";

    // 1. Inject Google Font
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap";
      document.head.appendChild(link);
    }

    // 2. Inject high-specificity CSS
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        [data-scene-script-view] *, 
        [data-scene-script-view] div, 
        [data-scene-script-view] span {
          font-family: ${COURIER_STACK} !important;
          -webkit-font-smoothing: antialiased;
        }
      `;
      document.head.appendChild(style);
    }
  }, [COURIER_STACK]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        <p className="text-sm font-medium text-zinc-500 animate-pulse">
          Loading script...
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative bg-[#fdfdfc] shadow-[0_10px_30px_rgba(0,0,0,0.04),0_1px_8px_rgba(0,0,0,0.02)] border border-zinc-100 rounded-sm md:rounded-md flex flex-col w-full max-w-[210mm] min-h-auto shrink-0 pb-24"
      style={{
        fontFamily: COURIER_STACK,
        paddingLeft: "1.5in",
        paddingRight: "1in",
        paddingTop: "1in",
        paddingBottom: "1in",
        lineHeight: "1.2",
      }}
      data-scene-script-view="true"
    >
      {blocks.map((block) => (
        <div
          key={block.id}
          className={`relative break-words w-full px-4 py-2 ${blockStyles[block.type].className}`}
          style={{
            ...blockStyles[block.type].inputStyle,
            minHeight: "1.5rem",
          }}
        >
          {block.type === "SCENE_HEADING" ? (
            <div className="flex items-center gap-2">
              {block.sceneNumber && (
                <span className="absolute -left-16 text-zinc-400 font-bold select-none">
                  {block.sceneNumber}
                </span>
              )}
              {block.sceneType && (
                <span className="font-bold">{block.sceneType}</span>
              )}
              <span className="font-bold">{block.text}</span>
              {block.timeOfDay && (
                <>
                  <span className="text-zinc-400">-</span>
                  <span className="font-bold">{block.timeOfDay}</span>
                </>
              )}
            </div>
          ) : (
            block.text
          )}
        </div>
      ))}
    </div>
  );
}
